import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMobileAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../services/api';

// Admin-only IMEI inspection panel. These values are deliberately absent
// from every normal order/swap response (json:"-" server-side) — this
// component hits the dedicated admin endpoints that are the single place
// they're ever returned, so it's used for warehouse inspection/verification.
const AdminIMEIViewer = ({ type, recordId, isDarkMode, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = type === 'swap'
          ? await adminAPI.getSwapIMEI(recordId)
          : await adminAPI.getOrderIMEI(recordId);
        if (!cancelled) setData(res?.data || res);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load IMEI data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type, recordId]);

  const panel = {
    background: isDarkMode ? '#17171f' : '#fff',
    color: isDarkMode ? '#f1f5f9' : '#1e293b',
  };
  const box = {
    padding: '0.85rem',
    borderRadius: '10px',
    marginBottom: '0.6rem',
    background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
  };
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.03em' };

  const renderIMEIPair = (imei1, imei2) => (
    <>
      <div style={mono}>{imei1 || '—'}</div>
      {imei2 && <div style={{ ...mono, opacity: 0.75, marginTop: '0.2rem' }}>{imei2} <span style={{ fontSize: '0.7rem', fontWeight: 600, opacity: 0.7 }}>(IMEI 2)</span></div>}
    </>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...panel, borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#22C55E' }} />
            {type === 'swap' ? 'Swap IMEI' : 'Order IMEI'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1.05rem' }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {loading && <div style={{ padding: '1.5rem', textAlign: 'center' }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>}
        {error && <div style={{ color: '#ef4444', fontSize: '0.9rem' }}>{error}</div>}

        {!loading && !error && data && type === 'order' && (
          <>
            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>Order {data.order_number}</div>
            {(data.items || []).map(item => (
              <div key={item.item_id} style={box}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.4rem' }}>
                  {item.product_title} <span style={{ opacity: 0.6, fontWeight: 400 }}>· Qty {item.quantity}</span>
                </div>
                {item.imei_submitted ? (
                  (item.imeis || []).map(u => (
                    <div key={u.unit_index} style={{ marginBottom: '0.5rem' }}>
                      {item.quantity > 1 && <div style={{ fontSize: '0.72rem', opacity: 0.6, marginBottom: '0.15rem' }}>Unit {u.unit_index}</div>}
                      {renderIMEIPair(u.imei1, u.imei2)}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>Not submitted by seller yet</div>
                )}
              </div>
            ))}
          </>
        )}

        {!loading && !error && data && type === 'swap' && (
          <>
            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>Swap {data.swap_number}</div>
            <div style={box}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.4rem' }}>Buyer / Initiator device</div>
              {data.initiator?.submitted
                ? renderIMEIPair(data.initiator.imei1, data.initiator.imei2)
                : <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>Not submitted</div>}
            </div>
            <div style={box}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.4rem' }}>Seller device</div>
              {data.seller?.submitted
                ? renderIMEIPair(data.seller.imei1, data.seller.imei2)
                : <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>Not submitted</div>}
            </div>
          </>
        )}

        <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', opacity: 0.65 }}>
          Visible to admin only — neither buyer nor seller can see these values.
        </div>
      </div>
    </div>
  );
};

export default AdminIMEIViewer;
